import * as React from "react";
import { Settings2 } from "lucide-react";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import { TeamSwitcher } from "./TeamSwitcher";
import { NavUser } from "./NavUser";
import { WorkspaceNav } from "./WorkspaceNav";
import { PatientRailNav } from "./PatientRailNav";
import { WorkspaceQuotaCard } from "./WorkspaceQuotaCard";
import { useWorkspacePersona } from "./individual/useWorkspacePersona";
import { useSelfPatientQuery } from "@/frontend/queries/dashboard/patients";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/frontend/components/ui/sidebar";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const params = useParams({ strict: false });
  const routePatientId = typeof params.patientId === "string" ? params.patientId : undefined;
  const isWorkspaceSettings = location.pathname === "/dashboard/settings";

  const { isIndividual } = useWorkspacePersona();
  const selfPatientQuery = useSelfPatientQuery(isIndividual && !routePatientId);
  const patientId = routePatientId ?? selfPatientQuery.data?.patientMembershipId;
  const isResolvingSelfPatient = isIndividual && !patientId && !selfPatientQuery.isError;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {patientId ? (
          <PatientRailNav patientId={patientId} />
        ) : isResolvingSelfPatient ? null : (
          <WorkspaceNav />
        )}
      </SidebarContent>

      <SidebarFooter className="gap-2">
        <WorkspaceQuotaCard />
        <SidebarSeparator className="mx-0 w-full" />
        {/* Collapsed, this group's px-2 would stack on the footer's p-2 and push the icon off-centre. */}
        <SidebarGroup className="py-0 group-data-[collapsible=icon]:px-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isWorkspaceSettings} tooltip="Settings">
                  <Link
                    to="/dashboard/settings"
                    search={{ section: "profile", destinationId: undefined, provider: "", webhookId: undefined }}
                  >
                    <Settings2 />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// Re-exported so _layout.tsx imports it from here.
export { SidebarInset };
